/**
 * Temporary Block IDs
 *
 * Blocks are added to the canvas optimistically, under a temporary ID, before the server has
 * created them. The user can keep working on such a block (edit, move, nest children in it,
 * delete it) while the request is in flight. Every API call therefore resolves IDs through
 * `resolveBlockId`, which waits for the creating request and returns the server's ID —
 * sending a temporary ID to the API would fail with 400/404.
 */

import type { EmailBlock } from "@/types/email";

export const TEMP_ID_PREFIX = "temp-";

const resolutions = new Map<string, Promise<string>>();

export function isTempId(id: string | null | undefined): boolean {
  return !!id && id.startsWith(TEMP_ID_PREFIX);
}

export function newTempId(): string {
  return `${TEMP_ID_PREFIX}${crypto.randomUUID()}`;
}

/**
 * Records the promise of the real ID for a temporary ID. Rejections are handled by whoever
 * awaits `resolveBlockId`; the creating action handles its own failure.
 */
export function registerTempId(tempId: string, realId: Promise<string>): void {
  realId.catch(() => undefined); // avoid unhandled-rejection noise when nobody is waiting
  resolutions.set(tempId, realId);
}

/**
 * Returns the server ID for `id`, waiting for the creating request if `id` is temporary.
 * Rejects if the block's creation failed (the caller's request must not be sent).
 */
export async function resolveBlockId(id: string): Promise<string> {
  if (!isTempId(id)) return id;
  const pending = resolutions.get(id);
  if (!pending) {
    throw new Error(`Block ${id} was never created on the server`);
  }
  return pending;
}

export async function resolveOptionalBlockId(id: string | null | undefined): Promise<string | undefined> {
  return id ? resolveBlockId(id) : undefined;
}

export function clearTempIds(): void {
  resolutions.clear();
}

/**
 * IDs of a block subtree in pre-order (parent before children, children in order). A duplicated
 * subtree has the same shape on client and server, so equal positions correspond.
 */
export function preorderIds(blocks: Pick<EmailBlock, "id" | "childBlocks">[]): string[] {
  const ids: string[] = [];
  const visit = (nodes: Pick<EmailBlock, "id" | "childBlocks">[]) => {
    for (const node of nodes) {
      ids.push(node.id);
      if (node.childBlocks?.length) visit(node.childBlocks);
    }
  };
  visit(blocks);
  return ids;
}

/**
 * Renames block IDs (and the parentBlockId references to them) throughout the tree, keeping
 * every other field — including local edits made while the request was in flight.
 */
export function renameBlockIds(tree: EmailBlock[], mapping: Map<string, string>): EmailBlock[] {
  if (mapping.size === 0) return tree;
  return tree.map((node) => ({
    ...node,
    id: mapping.get(node.id) ?? node.id,
    parentBlockId: node.parentBlockId ? mapping.get(node.parentBlockId) ?? node.parentBlockId : node.parentBlockId,
    childBlocks: node.childBlocks?.length ? renameBlockIds(node.childBlocks, mapping) : node.childBlocks,
  }));
}
